/**
 * Utility functions for PlayerPageHeader components
 *
 * Includes value formatting, date calculations, and data extraction helpers.
 * All functions are pure and follow immutable patterns.
 */

import { differenceInMonths, differenceInYears } from "date-fns";

import type { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";
import {
  flagUrlForNationality,
  resolveNationality,
  type FlagPlatform,
} from "@/utils/nationalityRegistry";

import {
  EMPTY_VALUE_PLACEHOLDER,
  INDICATOR_CONFIGS,
} from "./playerPageHeaderConstants";
import type { IndicatorMetricType } from "./playerPageHeaderTypes";

/**
 * Metric range configurations matching getPlayerCompositeMetrics.ts exactly
 * These use the same min/max values to produce identical normalized percentages
 */
const INDICATOR_RANGES: Record<
  // `potential` joins `transferValue` in the exclusion: both render a RANGE
  // rather than a point value, so there is no single number to normalize onto
  // a min/max scale. Both take an early return in getIndicatorValueColor.
  Exclude<IndicatorMetricType, "transferValue" | "potential">,
  {
    readonly min: number;
    readonly max: number;
  }
> = {
  gpr: { min: 30, max: 100 },
  playerStyleFit: { min: 40, max: 100 },
  teamStyleFit: { min: 40, max: 80 },
  // Inverted scale: fewer months = better (green), more months = worse (orange/red)
  // min=36 (bad, long contract), max=1 (good, expiring soon)
  monthsLeft: { min: 36, max: 1 },
};

/** Color scale for indicator text: Red (bad) -> Orange -> Mid (neutral) -> Green (good) */
const INDICATOR_COLORS_DARK = {
  red: [220, 38, 38] as const,
  orange: [234, 88, 12] as const,
  mid: [255, 255, 255] as const, // White for dark mode
  green: [34, 197, 94] as const,
} as const;

/** Light mode color scale: same red/orange/green but mid-point uses dark gray */
const INDICATOR_COLORS_LIGHT = {
  red: [220, 38, 38] as const,
  orange: [234, 88, 12] as const,
  mid: [63, 63, 70] as const, // Dark gray (zinc-800) for light mode
  green: [34, 197, 94] as const,
} as const;

/** Default text color for indicators */
const DEFAULT_INDICATOR_COLOR_DARK = "#FFFFFF";
const DEFAULT_INDICATOR_COLOR_LIGHT = "#3F3F46";

/**
 * Interpolate between two RGB colors
 * @param color1 - Start color as RGB tuple
 * @param color2 - End color as RGB tuple
 * @param factor - Interpolation factor (0-1)
 * @returns Interpolated RGB values
 */
function interpolateRgb(
  color1: readonly [number, number, number],
  color2: readonly [number, number, number],
  factor: number
): [number, number, number] {
  return [
    Math.round(color1[0] + (color2[0] - color1[0]) * factor),
    Math.round(color1[1] + (color2[1] - color1[1]) * factor),
    Math.round(color1[2] + (color2[2] - color1[2]) * factor),
  ];
}

/**
 * Convert RGB values to hex color string
 * @param rgb - RGB tuple
 * @returns Hex color string
 */
function rgbToHex(rgb: readonly [number, number, number]): string {
  return `#${rgb.map(c => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Calculate indicator text color using Red -> Orange -> Mid -> Green scale
 * @param normalizedValue - Value normalized to 0-100 range (0=bad, 100=good)
 * @param isDark - Whether dark mode is active (affects mid-point color)
 * @returns Hex color string
 * @remarks Scale: 0-25=red->orange, 25-50=orange->mid, 50-100=mid->green
 */
export function getIndicatorColorFromScale(
  normalizedValue: number,
  isDark: boolean
): string {
  const value = Math.max(0, Math.min(100, normalizedValue));
  const colors = isDark ? INDICATOR_COLORS_DARK : INDICATOR_COLORS_LIGHT;

  const rgb =
    value < 25
      ? interpolateRgb(colors.red, colors.orange, value / 25)
      : value < 50
        ? interpolateRgb(colors.orange, colors.mid, (value - 25) / 25)
        : interpolateRgb(colors.mid, colors.green, (value - 50) / 50);

  return rgbToHex(rgb);
}

/** Green color for all score indicators (GPR, PSF, TSF) */
const INDICATOR_GREEN = {
  dark: "#22C55E",
  light: "#16A34A",
} as const;

/**
 * Calculate the text color for an indicator value based on its metric type
 * @param type - The indicator metric type
 * @param value - The numeric value to evaluate
 * @param isDark - Whether dark mode is active (affects color scale)
 * @returns Hex color string for the value text
 * @remarks GPR, Player Style Fit, Team Style Fit all use green color
 */
export function getIndicatorValueColor(
  type: IndicatorMetricType,
  value: number | null | undefined,
  isDark: boolean
): string {
  // Null values get default color
  if (value == null) {
    return isDark
      ? DEFAULT_INDICATOR_COLOR_DARK
      : DEFAULT_INDICATOR_COLOR_LIGHT;
  }

  // GPR, Player Style Fit, Team Style Fit all use green color
  if (type === "gpr" || type === "playerStyleFit" || type === "teamStyleFit") {
    return isDark ? INDICATOR_GREEN.dark : INDICATOR_GREEN.light;
  }

  // Transfer value gets default color (handled separately via getTransferValueColor).
  // GPR Potential follows the same valuation path (SE-8154): it renders a range,
  // so there is no single value to colour on the gradient scale, and the PRD
  // specifies the same styling as the Gemini Player Valuation card. Returning
  // here also keeps it out of the INDICATOR_RANGES lookup below, which has no
  // `potential` entry by construction.
  if (type === "transferValue" || type === "potential") {
    return isDark
      ? DEFAULT_INDICATOR_COLOR_DARK
      : DEFAULT_INDICATOR_COLOR_LIGHT;
  }

  // Months left uses the gradient scale
  const range = INDICATOR_RANGES[type];
  const normalized = ((value - range.min) / (range.max - range.min)) * 100;

  return getIndicatorColorFromScale(normalized, isDark);
}

/** Transfer value color scheme - matches indicator color scale */
const TRANSFER_VALUE_COLORS = {
  underpriced: {
    dark: "#22C55E", // Green (good deal)
    light: "#16A34A",
  },
  overpriced: {
    dark: "#EA580C", // Orange (bad deal)
    light: "#C2410C",
  },
  fair: {
    dark: "#FFFFFF", // White (neutral)
    light: "#374151",
  },
} as const;

/**
 * Calculate transfer value color based on fee comparison (underpriced=green, overpriced=red)
 * @param expectedFee - Expected transfer fee (what buyer would pay)
 * @param fairFee - Fair market value fee (player's true worth)
 * @param isDark - Whether dark mode is active
 * @returns Hex color string for the value text
 * @remarks Uses same color scheme as feeComparisonColors.ts for consistency
 */
export function getTransferValueColor(
  expectedFee: number | null | undefined,
  fairFee: number | null | undefined,
  isDark: boolean
): string {
  if (!expectedFee || !fairFee) {
    return isDark
      ? DEFAULT_INDICATOR_COLOR_DARK
      : DEFAULT_INDICATOR_COLOR_LIGHT;
  }

  // Calculate percent difference (positive = overpriced, negative = underpriced)
  // expectedFee > fairFee = buyer pays more than worth = overpriced (bad)
  // expectedFee < fairFee = buyer pays less than worth = underpriced (good)
  const percentDiff = ((expectedFee - fairFee) / fairFee) * 100;

  // Match original feeComparisonColors.ts thresholds and colors
  if (percentDiff > 15) {
    return isDark
      ? TRANSFER_VALUE_COLORS.overpriced.dark
      : TRANSFER_VALUE_COLORS.overpriced.light;
  } else if (percentDiff < -15) {
    return isDark
      ? TRANSFER_VALUE_COLORS.underpriced.dark
      : TRANSFER_VALUE_COLORS.underpriced.light;
  } else {
    return isDark
      ? TRANSFER_VALUE_COLORS.fair.dark
      : TRANSFER_VALUE_COLORS.fair.light;
  }
}

/**
 * Format a numeric score value (0-100 scale)
 * @param value - The numeric value to format
 * @returns Formatted string or placeholder if null/undefined
 */
export function formatScoreValue(value: number | null | undefined): string {
  return value != null ? Math.round(value).toString() : EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Format a GPR Potential range as two whole scores joined by a spaced dash
 * (SE-8154) — e.g. floor 46.8 and ceiling 78.9 render as `47 - 79`.
 *
 * Both bounds go through {@link formatScoreValue}, so rounding matches every
 * other 0-100 score on the rail rather than inventing a second convention.
 * Callers gate on `hasGprPotential` before rendering the tile at all, so a
 * missing bound should never reach here; if one does, the placeholder shows on
 * that side rather than fabricating a number.
 * @param low - The floor bound
 * @param high - The ceiling bound
 * @returns The formatted range
 */
export function formatScoreRange(
  low: number | null | undefined,
  high: number | null | undefined
): string {
  return `${formatScoreValue(low)} - ${formatScoreValue(high)}`;
}

/**
 * Format a currency value with appropriate suffix (K, M, B)
 * @param value - The value in actual currency units
 * @returns Formatted string like "€25M", "€500K", or placeholder
 */
export function formatCurrencyMillions(
  value: number | null | undefined
): string {
  if (value == null) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  const absValue = Math.abs(value);

  if (absValue >= 1_000_000_000) {
    return `€${Math.floor(value / 1_000_000_000)}B`;
  } else if (absValue >= 1_000_000) {
    return `€${Math.round(value / 1_000_000)}M`;
  } else if (absValue >= 1_000) {
    return `€${Math.round(value / 1_000)}K`;
  }

  return `€${Math.round(value)}`;
}

/**
 * Format a transfer value range with currency symbols and unit suffixes.
 * @param low - Lower bound of the range in actual currency units
 * @param high - Upper bound of the range in actual currency units
 * @returns Formatted string like "€25M - €57M" or placeholder when either bound is missing
 */
export function formatTransferValueRange(
  low: number | null | undefined,
  high: number | null | undefined
): string {
  const lowFormatted = formatCurrencyMillions(low);
  const highFormatted = formatCurrencyMillions(high);

  return lowFormatted !== EMPTY_VALUE_PLACEHOLDER &&
    highFormatted !== EMPTY_VALUE_PLACEHOLDER
    ? `${lowFormatted} - ${highFormatted}`
    : EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Format the valuation tile display value based on available data.
 * Implements the 3-case fallback: GPV range > Public Valuation > placeholder.
 * @param transferValueLow - Lower bound of Gemini Player Valuation range
 * @param transferValueHigh - Upper bound of Gemini Player Valuation range
 * @param publicValuation - Public market valuation estimate
 * @returns Formatted string: "€71M - €85M" (GPV), "€25M" (public), or "—" (neither)
 */
export function formatValuationDisplay(
  transferValueLow: number | null | undefined,
  transferValueHigh: number | null | undefined,
  publicValuation: number | null | undefined
): string {
  const rangeFormatted = formatTransferValueRange(
    transferValueLow,
    transferValueHigh
  );

  return rangeFormatted !== EMPTY_VALUE_PLACEHOLDER
    ? rangeFormatted
    : formatCurrencyMillions(publicValuation);
}

/**
 * Calculate months remaining until a contract expires
 * @param contractExpires - ISO date string of contract expiration
 * @returns Number of months remaining or null if invalid/past
 * @remarks Uses date-fns differenceInMonths to match getPlayerCompositeMetrics.ts
 */
export function calculateMonthsRemaining(
  contractExpires: string | null | undefined
): number | null {
  if (!contractExpires) {
    return null;
  }

  const months = differenceInMonths(contractExpires, new Date());

  // Return null for past/expired contracts
  return months > 0 ? months : null;
}

/**
 * Format months remaining for display
 * @param contractExpires - ISO date string of contract expiration
 * @returns Formatted string like "18" or placeholder
 */
export function formatMonthsRemaining(
  contractExpires: string | null | undefined
): string {
  const months = calculateMonthsRemaining(contractExpires);
  return months != null ? months.toString() : EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Format indicator value based on metric type
 * @param type - The indicator metric type
 * @param value - The raw value (number, string range, or date)
 * @param additionalValue - Additional value for range types (e.g., high bound)
 * @returns Formatted string for display
 */
export function formatIndicatorValue(
  type: IndicatorMetricType,
  value: number | string | null | undefined,
  additionalValue?: number | null | undefined
): string {
  switch (type) {
    case "gpr":
    case "playerStyleFit":
    case "teamStyleFit":
      return formatScoreValue(typeof value === "number" ? value : null);
    case "potential":
      // Range-shaped like transferValue: `value` is the floor, `additionalValue`
      // the ceiling.
      return formatScoreRange(
        typeof value === "number" ? value : null,
        additionalValue
      );
    case "transferValue":
      return formatTransferValueRange(
        typeof value === "number" ? value : null,
        additionalValue
      );
    case "monthsLeft":
      return formatMonthsRemaining(typeof value === "string" ? value : null);
    default:
      return EMPTY_VALUE_PLACEHOLDER;
  }
}

/**
 * Extract positions string from player fragment
 * @param player - The player detail fragment
 * @returns Positions formatted like "CF / LW / RW" or placeholder
 */
export function getPlayerPositions(
  player: PlayerDetailPlayerMetaFragment | null | undefined
): string {
  const positions = player?.positions
    ?.map(p => p?.abbreviation)
    .filter(Boolean)
    .join(" / ");
  return positions || EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Extract birth year from date of birth
 * @param dateOfBirth - ISO date string (e.g., "1997-02-03")
 * @returns Two-digit year string (e.g., "97") or null
 */
export function extractBirthYear(
  dateOfBirth: string | null | undefined
): string | null {
  const year = dateOfBirth?.split("-")[0];
  return year ? `'${year.slice(2)}` : null;
}

/**
 * Format age with birth year
 * @param age - Age in years
 * @param dateOfBirth - ISO date string for birth year extraction
 * @returns Formatted string like "28 years old ('97)" or placeholder
 */
export function formatAgeWithBirthYear(
  age: number | null | undefined,
  dateOfBirth: string | null | undefined
): string {
  const birthYear = extractBirthYear(dateOfBirth);
  return age != null
    ? birthYear
      ? `${age} years old (${birthYear})`
      : `${age} years old`
    : EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Platform type for determining flag URL format
 */
export type PlatformOS = FlagPlatform;

/**
 * Build nationality flag URL from country name
 * @param nationality - Nationality object with name
 * @param platform - Platform OS to determine URL format (SVG for web, PNG for native)
 * @returns Flag CDN URL or null if nationality not found
 * @remarks Name resolution — case, diacritics, inverted names like
 *   "Korea, South", renames like "Turkiye", and the feed abbreviations "USA" /
 *   "UAE" — belongs to the shared resolver in `@/utils/nationalityRegistry`,
 *   so this surface and the Similar Players list can no longer disagree about
 *   which names have a flag (SE-6241). The alias table this function used to
 *   own moved there.
 */
export function getNationalityFlagUrl(
  nationality: { name?: string | null } | null | undefined,
  platform: PlatformOS
): string | null {
  const entry = resolveNationality(nationality?.name);

  return entry ? flagUrlForNationality(entry, platform) : null;
}

/**
 * Extract all identity data from player fragment
 * Age prefers bioData.age (provider-sourced players) and falls back to
 * calculating from dateOfBirth so that manually-created players — whose
 * bioData.age is always null — still show an age when a DOB was entered.
 * @param player - The player detail fragment
 * @param platform - Platform OS for flag URL format selection
 * @returns Object containing all identity-related display values
 */
export function extractPlayerIdentityData(
  player: PlayerDetailPlayerMetaFragment | null | undefined,
  platform: PlatformOS
): {
  readonly playerName: string;
  readonly roleArchetype: string | null;
  readonly secondaryRoleArchetype: string | null;
  readonly valuationStatus: string | null;
  readonly onLoanFrom: string | null;
  readonly positions: string;
  readonly clubName: string | null;
  readonly age: string | null;
  readonly birthYear: string | null;
  readonly nationality: string | null;
  readonly nationalityFlagUrl: string | null;
  readonly agency: string | null;
  readonly isInjured: boolean;
  readonly injuredUntil: string | null;
  readonly preferredFoot: string | null;
  readonly limitedDataWarning: boolean;
  readonly isManual: boolean;
} {
  const bioData = player?.bioData;
  const dobString = bioData?.dateOfBirth ?? player?.dateOfBirth;

  // DEBUG: Log injury data from backend
  // eslint-disable-next-line no-console -- Temporary debug logging
  console.log("[DEBUG] Player injury data:", {
    playerName: player?.name,
    isInjured: bioData?.isInjured,
    injuredUntil: bioData?.injuredUntil,
    foot: bioData?.foot,
  });

  return {
    playerName: player?.name ?? EMPTY_VALUE_PLACEHOLDER,
    roleArchetype: bioData?.roleArchetype ?? null,
    secondaryRoleArchetype: bioData?.secondaryRoleArchetype ?? null,
    valuationStatus: bioData?.valuationStatus ?? null,
    onLoanFrom: bioData?.onLoanFrom ?? null,
    positions: getPlayerPositions(player),
    clubName: player?.team?.name ?? player?.teamName ?? null,
    age:
      (
        bioData?.age ??
        (dobString ? differenceInYears(new Date(), new Date(dobString)) : null)
      )?.toString() ?? null,
    birthYear: extractBirthYear(dobString),
    nationality: player?.nationality?.name ?? null,
    nationalityFlagUrl: getNationalityFlagUrl(player?.nationality, platform),
    agency: bioData?.playerAgent ?? null,
    isInjured:
      bioData?.isInjured ??
      (bioData?.injuredUntil
        ? new Date(bioData.injuredUntil) > new Date()
        : false),
    injuredUntil: bioData?.injuredUntil ?? null,
    preferredFoot: bioData?.foot ?? null,
    limitedDataWarning: bioData?.limitedDataWarning ?? false,
    isManual: player?.isManual ?? false,
  };
}

/**
 * Extract all indicator data from player fragment
 * @param player - The player detail fragment
 * @returns Object containing all indicator-related values
 */
export function extractIndicatorData(
  player: PlayerDetailPlayerMetaFragment | null | undefined
): {
  readonly gpr: number | null;
  readonly playerStyleFit: number | null;
  readonly teamStyleFit: number | null;
  readonly transferValueLow: number | null;
  readonly transferValueHigh: number | null;
  readonly geminiMin: number | null;
  readonly geminiMax: number | null;
  readonly publicValuation: number | null;
  readonly contractExpires: string | null;
  readonly floorGpr: number | null;
  readonly ceilingGpr: number | null;
} {
  const bioData = player?.bioData;

  // fitScore is 0-1 from backend, multiply by 100 for display (matches getPlayerCompositeMetrics.ts)
  const fitScorePercent = bioData?.fitScore ? bioData.fitScore * 100 : null;

  // Use new Gemini valuation fields (min/max), fallback to deprecated fairFee/expectedFee
  const geminiMin =
    bioData?.minGeminiPlayerValuation ?? bioData?.fairFee ?? null;
  const geminiMax =
    bioData?.maxGeminiPlayerValuation ?? bioData?.expectedFee ?? null;

  // Ensure transfer values are ordered low-to-high for display
  const transferValueLow =
    geminiMin !== null && geminiMax !== null
      ? Math.min(geminiMin, geminiMax)
      : (geminiMin ?? geminiMax);
  const transferValueHigh =
    geminiMin !== null && geminiMax !== null
      ? Math.max(geminiMin, geminiMax)
      : null;

  return {
    gpr: bioData?.gpr ?? null,
    playerStyleFit: fitScorePercent,
    teamStyleFit: bioData?.teamStyleFit ?? null,
    transferValueLow,
    transferValueHigh,
    geminiMin,
    geminiMax,
    publicValuation: bioData?.playerValuation ?? null,
    contractExpires: bioData?.contractExpires ?? null,
    floorGpr: bioData?.floorGpr ?? null,
    ceilingGpr: bioData?.ceilingGpr ?? null,
  };
}

/**
 * Whether a player is eligible for the GPR Potential tile (SE-8155).
 *
 * Eligibility on the frontend collapses to "both bounds present" — the age and
 * season checks that actually select the ~10.8% covered cohort live in the
 * model, and a player outside it comes back with nulls. The PRD is explicit
 * that a miss shows nothing at all: not an empty card, not a placeholder, not
 * a dash. So this is the single gate the tile is pushed behind, and callers
 * must omit the tile entirely rather than render EMPTY_VALUE_PLACEHOLDER.
 *
 * @param floorGpr - Projected GPR at peak age, 25th percentile
 * @param ceilingGpr - Projected GPR at peak age, 99th percentile
 * @returns True only when both bounds are present
 */
export function hasGprPotential(
  floorGpr: number | null | undefined,
  ceilingGpr: number | null | undefined
): boolean {
  return floorGpr != null && ceilingGpr != null;
}

/**
 * Build the GPR Potential rail entry, or nothing when the player is outside the
 * model's coverage (SE-8154).
 *
 * Returns a 0-or-1 element array so the caller can spread it straight into the
 * indicators array at the position the PRD wants (index 1, right of GPR). An
 * ineligible player yields an empty array — the tile is OMITTED, never rendered
 * with a placeholder.
 * @param floorGpr - Projected GPR at peak age, 25th percentile
 * @param ceilingGpr - Projected GPR at peak age, 99th percentile
 * @param isDark - Whether dark mode is active
 * @param onPress - Opens the routed breakdown
 * @returns A single-entry array when eligible, otherwise empty
 */
export function buildPotentialIndicators(
  floorGpr: number | null,
  ceilingGpr: number | null,
  isDark: boolean,
  onPress: () => void
): readonly {
  readonly type: "potential";
  readonly label: string;
  readonly value: string;
  readonly valueColor: string;
  readonly onPress: () => void;
  readonly showArrow: boolean;
}[] {
  if (!hasGprPotential(floorGpr, ceilingGpr)) {
    return [];
  }

  return [
    {
      type: "potential",
      label: INDICATOR_CONFIGS.potential.shortLabel,
      value: formatScoreRange(floorGpr, ceilingGpr),
      valueColor: getIndicatorValueColor("potential", floorGpr, isDark),
      // No hasTransferValue-style guard: the tile exists only when its data
      // does, so the arrow can never advertise a dead breakdown.
      onPress,
      showArrow: true,
    },
  ];
}
